import { Controller } from "react-hook-form";
import { HiArrowRight } from "react-icons/hi2";
import { Tooltip } from "@mantine/core";
import CustomLoader from "../../ui/CustomLoader/CustomLoader";
import CustomNumberInput from "../../ui/CustomNumberInput/CustomNumberInput";
import CustomSelect from "../../ui/CustomSelect/CustomSelect";
import CustomButton from "../../ui/CustomButton/CustomButton";
import { CustomText } from "../../ui/CustomText/CustomText";
import { PAYOUT_ACCOUNT_TYPES } from "../config/fundsTransferConfig";
import { FundsTransferValidators } from "../validations/FundsTransferValidations";
import { optionsFilterByLabel } from "../../../utils/optionsFilter";
import {
  exceedsBalance,
  formatKES,
  getRemainingLimitColor,
} from "../../../utils/transactionutils";
import { renderMobileMoneyOption } from "../../shared/mobileMoneyProviders";

function WithdrawalAmountForm({
  control,
  onNext,
  payoutAccountOptions,
  isAccountsLoading,
  isAccountsError,
  refetchAccounts,
  accountLabel,
  accountPlaceholder,
  accountRenderOption,
  walletBalance,
  isBalanceError,
  isChargesFetching,
  isFormLocked,
  watchedAmount,
  watchedSelectedPayoutAccountId,
  showLimitsBanner,
  disbursementLimits,
  isLimitsLoading,
}) {
  const hasAccount = !!watchedSelectedPayoutAccountId;
  const hasAmount = !!watchedAmount;
  const canProceed = !isFormLocked && hasAccount && hasAmount;

  let tooltipLabel = null;
  if (!hasAccount) tooltipLabel = "Select an account to continue";
  else if (!hasAmount) tooltipLabel = "Enter an amount to continue";

  const isLimitExceeded =
    disbursementLimits?.availableDailyLimit != null &&
    Number(watchedAmount) > disbursementLimits.availableDailyLimit;
  const remainingLimitColor = getRemainingLimitColor(
    disbursementLimits,
    isLimitExceeded,
  );

  return (
    <div className="space-y-4">
      {showLimitsBanner && (
        <div className="flex flex-col gap-1.5 bg-gray-50 rounded-lg px-3 py-2">
          <span className="text-sm text-gray-500">
            Institutional Daily Limit:{" "}
            {isLimitsLoading ? (
              <span className="text-gray-400">Loading…</span>
            ) : disbursementLimits ? (
              <span className="text-gray-900 font-medium">
                {formatKES(disbursementLimits.dailyLimit)}
              </span>
            ) : (
              <span className="text-gray-400">—</span>
            )}
          </span>
          <span className="text-sm text-gray-500">
            Today&apos;s Remaining Limit:{" "}
            {isLimitsLoading ? (
              <span className="text-gray-400">Loading…</span>
            ) : disbursementLimits ? (
              <span className={`${remainingLimitColor} font-medium`}>
                {formatKES(disbursementLimits.availableDailyLimit)}
              </span>
            ) : (
              <span className="text-gray-400">—</span>
            )}
          </span>
        </div>
      )}

      <Controller
        name="selectedPayoutAccountId"
        control={control}
        rules={{
          validate: (v) =>
            FundsTransferValidators.selectedPayoutAccountId(v) ?? true,
        }}
        render={({ field, fieldState }) => (
          <CustomSelect
            label={accountLabel}
            withAsterisk
            radius="md"
            searchable
            lockOnSelect
            placeholder={
              isAccountsLoading ? "Loading accounts..." : accountPlaceholder
            }
            data={payoutAccountOptions}
            renderOption={accountRenderOption}
            filter={optionsFilterByLabel}
            disabled={isFormLocked}
            rightSection={
              isAccountsLoading ? <CustomLoader color="primary" /> : undefined
            }
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            error={fieldState.error?.message}
          />
        )}
      />

      {isAccountsError && (
        <div className="flex items-center justify-between gap-2 px-1">
          <CustomText c="error" size="sm">
            Failed to load accounts.
          </CustomText>
          <CustomButton color="primary" size="xs" onClick={refetchAccounts}>
            Retry
          </CustomButton>
        </div>
      )}

      {!isAccountsError &&
        payoutAccountOptions.length === 0 &&
        !isAccountsLoading && (
          <CustomText c="error" size="sm">
            No accounts available. Please add an account first.
          </CustomText>
        )}

      <Controller
        name="amount"
        control={control}
        rules={{
          validate: {
            format: (v) =>
              FundsTransferValidators.amount(v, disbursementLimits) ?? true,
            balance: (v) =>
              walletBalance != null && exceedsBalance(v, walletBalance)
                ? `Insufficient balance. Available: ${formatKES(walletBalance)}`
                : true,
            dailyLimit: (v) =>
              disbursementLimits?.availableDailyLimit != null &&
              Number(v) > disbursementLimits.availableDailyLimit
                ? `Amount exceeds your available daily limit of ${formatKES(disbursementLimits.availableDailyLimit)}.`
                : true,
          },
        }}
        render={({ field, fieldState }) => (
          <div className="space-y-1">
            <CustomNumberInput
              label="Amount to Withdraw (Ksh)"
              withAsterisk
              min={disbursementLimits?.minAmount ?? 1}
              clampBehavior="none"
              radius="md"
              placeholder="Enter amount"
              disabled={isFormLocked}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              error={fieldState.error?.message}
            />
            {showLimitsBanner && disbursementLimits && (
              <p className="text-xs text-gray-400 px-1">
                Min: {formatKES(disbursementLimits.minAmount)} · Max:{" "}
                {formatKES(disbursementLimits.maxAmount)}
              </p>
            )}
          </div>
        )}
      />

      {isBalanceError && (
        <p className="text-xs text-amber-600 px-1">
          Balance unavailable — real-time validation disabled.
        </p>
      )}

      <Tooltip
        label={tooltipLabel ?? ""}
        withArrow
        position="top"
        disabled={canProceed || !tooltipLabel}
      >
        <span className="block">
          <CustomButton
            color="primary"
            radius="md"
            rightSection={<HiArrowRight size={16} />}
            onClick={onNext}
            disabled={!canProceed}
            loading={isChargesFetching}
            fullWidth
          >
            Next: Review Details
          </CustomButton>
        </span>
      </Tooltip>
    </div>
  );
}

export function Step1Details({
  withdrawalType,
  control,
  onNext,
  payoutAccountOptions,
  isAccountsLoading,
  isAccountsError,
  refetchAccounts,
  walletBalance,
  isBalanceError,
  isChargesFetching,
  isFormLocked,
  watchedAmount,
  watchedSelectedPayoutAccountId,
  disbursementLimits,
  isLimitsLoading,
}) {
  const sharedProps = {
    control,
    onNext,
    payoutAccountOptions,
    isAccountsLoading,
    isAccountsError,
    refetchAccounts,
    walletBalance,
    isBalanceError,
    isChargesFetching,
    isFormLocked,
    watchedAmount,
    watchedSelectedPayoutAccountId,
    disbursementLimits,
    isLimitsLoading,
  };

  switch (withdrawalType) {
    case PAYOUT_ACCOUNT_TYPES.MOBILE_MONEY:
      return (
        <WithdrawalAmountForm
          {...sharedProps}
          accountLabel="Mobile Money Account"
          accountPlaceholder="Search by provider, phone number or name"
          accountRenderOption={renderMobileMoneyOption}
          showLimitsBanner
        />
      );
    case PAYOUT_ACCOUNT_TYPES.BANK:
      return (
        <WithdrawalAmountForm
          {...sharedProps}
          accountLabel="Bank Account"
          accountPlaceholder="Search by bank name, account number or account name"
          showLimitsBanner
        />
      );
    case PAYOUT_ACCOUNT_TYPES.WALLET:
      return (
        <WithdrawalAmountForm
          {...sharedProps}
          accountLabel="Paykit Wallet"
          accountPlaceholder="Select Paykit wallet"
        />
      );
    default:
      return null;
  }
}
